<template>
  <div class="vue-html2pdf">
    <section
      class="layout-container"
      :class="{
        'show-layout': showLayout,
        'unset-all': !floatLayout
      }"
    >
      <section
        class="content-wrapper"
        :style="`width: ${pdfContentWidth};`"
        ref="pdfContent"
      >
        <slot name="pdf-content" />
      </section>
    </section>

    <transition name="transition-anim">
      <section class="pdf-preview" v-if="pdfFile">
        <button @click.self="closePreview()">
          &times;
        </button>

        <iframe :src="pdfFile" width="100%" height="100%" />
      </section>
    </transition>
  </div>
</template>

<script>
// Local replacement for vue-html2pdf 1.8.0 (same props, events and markup), which pinned the
// vulnerable html2pdf.js 0.9 / jsPDF 1.5. html2pdf.js is loaded on demand so it stays out of the main bundle.
// The build's publicPath is relative, so point the chunk request at the served bundle folder for this load only.
/* global __webpack_public_path__:writable */
/* eslint-disable no-global-assign */
const loadHtml2pdf = () => {
  const previousPublicPath = __webpack_public_path__;
  __webpack_public_path__ = "/Content/SelfAssessmentTool/";
  const chunk = import(/* webpackChunkName: "html2pdf" */ "html2pdf.js");
  __webpack_public_path__ = previousPublicPath;
  return chunk.then(m => m.default || m);
};
/* eslint-enable no-global-assign */

export default {
  name: "PdfExport",
  props: {
    showLayout: {
      type: Boolean,
      default: false
    },
    floatLayout: {
      type: Boolean,
      default: true
    },
    enableDownload: {
      type: Boolean,
      default: true
    },
    previewModal: {
      type: Boolean,
      default: false
    },
    paginateElementsByHeight: {
      type: Number
    },
    filename: {
      type: String,
      default: `${new Date().getTime()}`
    },
    pdfQuality: {
      type: Number,
      default: 2
    },
    pdfFormat: {
      default: "a4"
    },
    pdfOrientation: {
      type: String,
      default: "portrait"
    },
    pdfContentWidth: {
      default: "800px"
    },
    htmlToPdfOptions: {
      type: Object
    },
    manualPagination: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      hasAlreadyParsed: false,
      progress: 0,
      pdfFile: null
    };
  },
  watch: {
    progress(val) {
      this.$emit("progress", val);
    },
    paginateElementsByHeight() {
      this.resetPagination();
    }
  },
  methods: {
    resetPagination() {
      const parentElement = this.$refs.pdfContent.firstChild;
      const pageBreaks = parentElement.getElementsByClassName("html2pdf__page-break");
      const pageBreakLength = pageBreaks.length - 1;

      if (pageBreakLength === -1) return;

      this.hasAlreadyParsed = false;

      for (let x = pageBreakLength; x >= 0; x--) {
        pageBreaks[x].parentNode.removeChild(pageBreaks[x]);
      }
    },
    generatePdf() {
      this.$emit("startPagination");
      this.progress = 0;
      this.paginationOfElements();
    },
    paginationOfElements() {
      this.progress = 25;

      // With manual pagination, html2pdf.js breaks pages at elements with the "html2pdf__page-break" class
      if (this.manualPagination) {
        this.progress = 70;
        this.$emit("hasPaginated");
        this.downloadPdf();
        return;
      }

      if (!this.hasAlreadyParsed) {
        const parentElement = this.$refs.pdfContent.firstChild;
        const childElements = Array.from(parentElement.children);
        let childrenHeight = 0;

        // Insert a page break before the first element that would take the page past paginateElementsByHeight
        for (const childElement of childElements) {
          if (childElement.classList[0] === "html2pdf__page-break") {
            childrenHeight = 0;
          } else {
            const elementHeight = childElement.clientHeight;
            const elementComputedStyle = childElement.currentStyle || window.getComputedStyle(childElement);
            const elementMarginTopBottom =
              parseInt(elementComputedStyle.marginTop) + parseInt(elementComputedStyle.marginBottom);
            const elementHeightWithMargin = elementHeight + elementMarginTopBottom;

            if (childrenHeight + elementHeight < this.paginateElementsByHeight) {
              childrenHeight += elementHeightWithMargin;
            } else {
              const section = document.createElement("div");
              section.classList.add("html2pdf__page-break");
              parentElement.insertBefore(section, childElement);
              childrenHeight = elementHeightWithMargin;
            }
          }
        }

        this.progress = 70;
        this.hasAlreadyParsed = true;
      } else {
        this.progress = 70;
      }

      this.$emit("hasPaginated");
      this.downloadPdf();
    },
    async downloadPdf() {
      const html2pdf = await loadHtml2pdf();
      const pdfContent = this.$refs.pdfContent;
      const options = this.setOptions();

      this.$emit("beforeDownload", { html2pdf, options, pdfContent });

      const html2PdfSetup = html2pdf().set(options).from(pdfContent);
      let pdfBlobUrl = null;

      if (this.previewModal) {
        this.pdfFile = await html2PdfSetup.output("bloburl");
        pdfBlobUrl = this.pdfFile;
      }

      if (this.enableDownload) {
        pdfBlobUrl = await html2PdfSetup.save().output("bloburl");
      }

      if (pdfBlobUrl) {
        const res = await fetch(pdfBlobUrl);
        const blobFile = await res.blob();
        this.$emit("hasDownloaded", blobFile);
      }

      this.progress = 100;
    },
    setOptions() {
      if (this.htmlToPdfOptions !== undefined && this.htmlToPdfOptions !== null) {
        return this.htmlToPdfOptions;
      }

      return {
        margin: 0,
        filename: `${this.filename}.pdf`,
        image: {
          type: "jpeg",
          quality: 0.98
        },
        enableLinks: false,
        html2canvas: {
          scale: this.pdfQuality,
          useCORS: true
        },
        jsPDF: {
          unit: "in",
          format: this.pdfFormat,
          orientation: this.pdfOrientation
        }
      };
    },
    closePreview() {
      this.pdfFile = null;
    }
  }
};
</script>

<style lang="scss" scoped>
.vue-html2pdf {
  .layout-container {
    position: fixed;
    width: 100vw;
    height: 100vh;
    left: -100vw;
    top: 0;
    z-index: -9999;
    background: rgba(95, 95, 95, 0.8);
    display: flex;
    justify-content: center;
    align-items: flex-start;
    overflow: auto;

    &.show-layout {
      left: 0vw;
      z-index: 9999;
    }

    &.unset-all {
      all: unset;
      width: auto;
      height: auto;
    }
  }

  .pdf-preview {
    position: fixed;
    width: 65%;
    min-width: 600px;
    height: 80vh;
    top: 100px;
    z-index: 9999999;
    left: 50%;
    transform: translateX(-50%);
    border-radius: 5px;
    box-shadow: 0px 0px 10px #00000048;

    button {
      position: absolute;
      top: -20px;
      left: -15px;
      width: 35px;
      height: 35px;
      background: #555;
      border: 0;
      box-shadow: 0px 0px 10px #00000048;
      border-radius: 50%;
      color: #fff;
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: 20px;
      cursor: pointer;
    }

    iframe {
      border: 0;
    }
  }

  .transition-anim-enter-active,
  .transition-anim-leave-active {
    transition: opacity 0.3s ease-in;
  }

  .transition-anim-enter,
  .transition-anim-leave-to {
    opacity: 0;
  }
}
</style>
